// Worker management utilities for the Dot Counter application
import { v4 as uuidv4 } from 'uuid';

// Add module declaration for uuid
declare module 'uuid' {
  export function v4(): string;
}

// Worker message types
export interface WorkerParams {
  canvasMode?: boolean; // Whether to use OffscreenCanvas mode
  id: string;  // To identify the request
  imageData?: ImageData; // Regular ImageData if not using OffscreenCanvas
  canvas?: OffscreenCanvas; // OffscreenCanvas when using canvas mode
  params: {
    threshold: number;
    saturationMin: number;
    minArea: number;
    maxArea: number;
    minDistance: number;
    showBounds: boolean;
  };
  restrictionPolygon?: {x: number, y: number}[]; // Lasso area restriction
  mode: 'sweep' | 'single';  // 'sweep' for parameter sweep, 'single' for single run
  chunkSize?: number;  // Number of parameter sets to process in each chunk
  yieldInterval?: number;  // How many items to process before yielding
}

export interface WorkerProgress {
  id: string;
  progress: number;  // 0-1
  type: 'progress';
}

export interface WorkerResult {
  id: string;
  type: 'result';
  estimatedCount: number;
  lowerBound: number;
  upperBound: number;
  confidence: number;
  stableRegionSize: number;
  totalTrials: number;
  optimalParams?: Record<string, any>;
}

export interface WorkerError {
  id: string;
  type: 'error';
  message: string;
}

export type WorkerResponse = WorkerProgress | WorkerResult | WorkerError;

export interface RobustCounterWorkerManager {
  runRobustCount: (
    imageData: ImageData | HTMLCanvasElement,
    params: WorkerParams['params'],
    restrictionPolygon?: {x: number, y: number}[],
    onProgress?: (progress: number) => void
  ) => Promise<WorkerResult>;
  sweepParameters: (
    imageData: ImageData | HTMLCanvasElement,
    baseParams: WorkerParams['params'],
    restrictionPolygon?: {x: number, y: number}[],
    onProgress?: (progress: number) => void
  ) => Promise<WorkerResult>;
  terminateWorker: () => void;
}

// Create a manager for the robust counter worker
export function createRobustCounterWorkerManager(): RobustCounterWorkerManager {
  // Create the worker
  let worker: Worker | null = null;
  let currentAbortController: AbortController | null = null;

  // Initialize or get the worker
  const getWorker = (): Worker => {
    if (!worker) {
      worker = new Worker(new URL('../workers/robustCountingWorker.ts', import.meta.url), {
        type: 'module'
      });
    }
    return worker;
  };

  // Run a single robust count with specific parameters
  const runRobustCount = (
    input: ImageData | HTMLCanvasElement,
    params: WorkerParams['params'],
    restrictionPolygon?: {x: number, y: number}[],
    onProgress?: (progress: number) => void
  ): Promise<WorkerResult> => {
    // Cancel any existing operation
    if (currentAbortController) {
      currentAbortController.abort();
    }

    // Create new abort controller
    currentAbortController = new AbortController();
    const signal = currentAbortController.signal;

    return new Promise<WorkerResult>((resolve, reject) => {
      const worker = getWorker();
      const requestId = uuidv4();

      // Setup message handler
      const messageHandler = (e: MessageEvent<WorkerResponse>) => {
        const response = e.data;
        
        // Make sure this is our response
        if (response.id !== requestId) return;

        if (response.type === 'progress' && onProgress) {
          onProgress(response.progress);
        } else if (response.type === 'result') {
          cleanup();
          resolve(response as WorkerResult);
        } else if (response.type === 'error') {
          cleanup();
          reject(new Error((response as WorkerError).message));
        }
      };

      // Setup abort handler
      const abortHandler = () => {
        cleanup();
        reject(new Error('Operation was cancelled'));
      };

      // Clean up event listeners
      const cleanup = () => {
        worker.removeEventListener('message', messageHandler);
        signal.removeEventListener('abort', abortHandler);
      };

      // Set up listeners
      worker.addEventListener('message', messageHandler);
      signal.addEventListener('abort', abortHandler);

      // Send the request to the worker
      const isCanvas = input instanceof HTMLCanvasElement;
      
      if (isCanvas && 'transferControlToOffscreen' in input) {
        // Use OffscreenCanvas when available
        const offscreenCanvas = (input as any).transferControlToOffscreen();
        worker.postMessage({
          id: requestId,
          canvas: offscreenCanvas,
          canvasMode: true,
          params,
          restrictionPolygon,
          mode: 'single'
        }, [offscreenCanvas]);
      } else {
        // Fallback to ImageData
        const imageData = isCanvas 
          ? input.getContext('2d')?.getImageData(0, 0, input.width, input.height)
          : input;
        
        if (!imageData) {
          reject(new Error('Failed to get image data from canvas'));
          return;
        }
        
        worker.postMessage({
          id: requestId,
          imageData,
          params,
          restrictionPolygon,
          mode: 'single'
        } as WorkerParams);
      }
    });
  };

  // Sweep parameters to find optimal settings
  const sweepParameters = (
    input: ImageData | HTMLCanvasElement,
    baseParams: WorkerParams['params'],
    restrictionPolygon?: {x: number, y: number}[],
    onProgress?: (progress: number) => void
  ): Promise<WorkerResult> => {
    // Cancel any existing operation
    if (currentAbortController) {
      currentAbortController.abort();
    }

    // Create new abort controller
    currentAbortController = new AbortController();
    const signal = currentAbortController.signal;

    return new Promise<WorkerResult>((resolve, reject) => {
      const worker = getWorker();
      const requestId = uuidv4();

      // Setup message handler
      const messageHandler = (e: MessageEvent<WorkerResponse>) => {
        const response = e.data;
        
        // Make sure this is our response
        if (response.id !== requestId) return;

        if (response.type === 'progress' && onProgress) {
          onProgress(response.progress);
        } else if (response.type === 'result') {
          cleanup();
          resolve(response as WorkerResult);
        } else if (response.type === 'error') {
          cleanup();
          reject(new Error((response as WorkerError).message));
        }
      };

      // Setup abort handler
      const abortHandler = () => {
        cleanup();
        reject(new Error('Operation was cancelled'));
      };

      // Clean up event listeners
      const cleanup = () => {
        worker.removeEventListener('message', messageHandler);
        signal.removeEventListener('abort', abortHandler);
      };

      // Set up listeners
      worker.addEventListener('message', messageHandler);
      signal.addEventListener('abort', abortHandler);

      // Send the request to the worker
      const isCanvas = input instanceof HTMLCanvasElement;
      
      if (isCanvas && 'transferControlToOffscreen' in input) {
        // Use OffscreenCanvas when available
        const offscreenCanvas = (input as any).transferControlToOffscreen();
        worker.postMessage({
          id: requestId,
          canvas: offscreenCanvas,
          canvasMode: true,
          params: baseParams,
          restrictionPolygon,
          mode: 'sweep',
          chunkSize: 10,
          yieldInterval: 5
        }, [offscreenCanvas]);
      } else {
        // Fallback to ImageData
        const imageData = isCanvas 
          ? input.getContext('2d')?.getImageData(0, 0, input.width, input.height)
          : input;
        
        if (!imageData) {
          reject(new Error('Failed to get image data from canvas'));
          return;
        }
        
        worker.postMessage({
          id: requestId,
          imageData,
          params: baseParams,
          restrictionPolygon,
          mode: 'sweep',
          chunkSize: 10,
          yieldInterval: 5
        } as WorkerParams);
      }
    });
  };

  // Terminate the worker
  const terminateWorker = () => {
    if (worker) {
      worker.terminate();
      worker = null;
    }
  };

  return {
    runRobustCount,
    sweepParameters,
    terminateWorker
  };
}

export default createRobustCounterWorkerManager;
